import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Order } from "../../interfaces/order";
import { OrderForm } from "../../interfaces/orderForm";
import { OrderFormType } from "../../interfaces/orderFormType";
import { CriminalOrder } from "../../interfaces/criminalOrder";
import { ReferenceOrder } from "../../interfaces/referenceOrder";
import { ReferenceDetail } from "../../interfaces/referenceDetaill";
import { Service } from "../../interfaces/service";
import { loadReferenceLetters } from "../../services/referenceLetterService";
import { loadOrderData } from "../../services/orderService";
import { ReferenceLetter } from "../../interfaces/referenceLetter";
import ordinal from "ordinal";
import { HttpService } from "../../services/httpService";
import { formatDate, sortOrderServices } from "../../services/helpers";
import ToolTip from "../../components/component-tools/ToolTip";
import { OrderService } from "../../interfaces/orderService";
import { downloadReferenceResponses } from "../../services/referenceDetailService";
import { CriminalDetail } from "../../interfaces/criminalDetail";
import { SubscriptionOrder } from "../../interfaces/subscriptionOrder";
import { SubscriptionDetail } from "../../interfaces/subscriptionDetail";
import { checkForOpenSubOrder } from "../../services/subscriptionOrderService";

const ClientViewOrder = () => {
  const [errorMessage, setErrorMessage] = useState("");
  const { orderId } = useParams();
  const [order, setOrder] = useState<Order>();
  const [orderForms, setOrderForms] = useState<OrderForm[]>([]);
  const [orderFormTypes, setOrderFormTypes] = useState<OrderFormType[]>([]);
  const [criminalOrder, setCriminalOrder] = useState<CriminalOrder>();
  const [criminalDetails, setCriminalDetails] = useState<CriminalDetail[]>([]); //visible non-add-on crim details
  const [visibleSubCriminalDetails, setVisibleSubCriminalDetails] = useState<
    CriminalDetail[]
  >([]); //visible add-on crim details
  const [subscriptionOrder, setSubscriptionOrder] =
    useState<SubscriptionOrder>();
  const [subscriptionDetails, setSubscriptionDetails] = useState<
    SubscriptionDetail[]
  >([]);
  const [referenceOrder, setReferenceOrder] = useState<ReferenceOrder>();
  const [referenceDetails, setReferenceDetails] = useState<ReferenceDetail[]>(
    [],
  );
  const [services, setServices] = useState<Service[]>([]);
  const [allLetters, setAllLetters] = useState<
    Record<number, ReferenceLetter[]>
  >({});
  const [showLettersFor, setShowLettersFor] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [signedUrls, setSignedUrls] = useState<Record<number, string>>({});
  const [criminalFinalUrl, setCriminalFinalUrl] = useState<string | null>(null);
  const [referenceFinalUrl, setReferenceFinalUrl] = useState<string | null>(
    null,
  );
  const [criminalDetailUrls, setCriminalDetailUrls] = useState<
    { id: number; signedUrl: string | null }[]
  >([]);
  const [referenceDetailUrls, setReferenceDetailUrls] = useState<
    { id: number; signedUrl: string | null }[]
  >([]);
  const [visibleSubCriminalDetailUrls, setVisibleSubCriminalDetailUrls] =
    useState<{ id: number; signedUrl: string | null }[]>([]);
  const [orderServices, setOrderServices] = useState<OrderService[]>([]);
  const navigate = useNavigate();
  const [checkAll, setCheckAll] = useState(false);
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [openSubOrder, setOpenSubOrder] = useState(false);

  const containerMap: Record<number, string> = useMemo(
    () => ({
      1: "background-check-agreements",
      2: "criminal-records-authorizations",
      3: "credit-report-authorizations",
      4: "motor-vehicle-authorizations",
      5: "reference-screening-authorizations",
      6: "candidate-screening-questionnaires",
      7: "reference-identification-forms",
      8: "transfer-releases",
    }),
    [],
  );

  useEffect(() => {
    const fetchOrderData = async () => {
      try {
        if (orderId) {
          const numericOrderId = parseInt(orderId, 10);
          const {
            order,
            orderForms,
            orderFormTypes,
            criminalOrder,
            criminalDetails,
            visibleSubCriminalDetails,
            subscriptionOrder,
            subscriptionDetails,
            referenceOrder,
            referenceDetails,
            services,
            orderServices,
          } = await loadOrderData(numericOrderId);

          if (order.orderStatusID === 1 || order.orderStatusID === 5) {
            navigate(`/client/current-orders`);
          }

          setOrder(order);
          setOrderForms(orderForms);
          setOrderFormTypes(orderFormTypes);
          setCriminalOrder(criminalOrder);
          setCriminalDetails(criminalDetails);
          setVisibleSubCriminalDetails(visibleSubCriminalDetails);
          setSubscriptionOrder(subscriptionOrder);
          setSubscriptionDetails(subscriptionDetails);
          setReferenceOrder(referenceOrder);
          // const filtered = referenceDetails.filter((d) => d.adminReviewed);
          const sorted = referenceDetails.sort((a, b) => {
            const nameA = a.placeName?.trim() || "";
            const nameB = b.placeName?.trim() || "";
            const isEmptyA = nameA === "";
            const isEmptyB = nameB === "";
            if (isEmptyA && !isEmptyB) return 1;
            if (!isEmptyA && isEmptyB) return -1;
            return nameA.localeCompare(nameB);
          });
          setReferenceDetails(sorted);
          setServices(services);
          setServices(services);
          setOrderServices(orderServices);

          //get the urls
          const urlPromises = orderForms.map(async (form) => {
            const container = containerMap[form.orderFormTypeID];
            if (container && order && form.completedFormUrl) {
              try {
                const signedUrl = await HttpService.getFile(
                  container,
                  form.orderFormID.toString(),
                  form.completedFormUrl,
                );
                return { id: form.orderFormID, signedUrl };
              } catch (error) {
                console.error(
                  `Failed to fetch signed URL for form ID ${form.orderFormID}:`,
                  error,
                );
                return { id: form.orderFormID, signedUrl: null };
              }
            } else {
              console.warn(
                `No container mapping for form ID ${form.orderFormID}`,
              );
              return { id: form.orderFormID, signedUrl: null };
            }
          });

          //get final report urls
          try {
            if (criminalOrder.finalReportUrl) {
              const signedCriminalUrl = await HttpService.getFile(
                "criminal-final-reports",
                criminalOrder.criminalOrderID.toString(),
                criminalOrder.finalReportUrl,
              );
              setCriminalFinalUrl(signedCriminalUrl);
            }
            if (referenceOrder.finalReportUrl) {
              const signedReferenceUrl = await HttpService.getFile(
                "reference-final-reports",
                referenceOrder.referenceOrderID.toString(),
                referenceOrder.finalReportUrl,
              );
              setReferenceFinalUrl(signedReferenceUrl);
            }
          } catch (error) {
            console.error(
              `Failed to fetch signed URL for final report(s)`,
              error,
            );
          }

          //get visible sub criminal detail urls
          try {
            const urlPromises = visibleSubCriminalDetails.map(
              async (detail) => {
                if (detail.receivedFormUrl) {
                  try {
                    const signedUrl = await HttpService.getFile(
                      "criminal-detail-forms",
                      detail.criminalDetailID.toString(),
                      detail.receivedFormUrl,
                    );
                    return { id: detail.criminalDetailID, signedUrl };
                  } catch (error) {
                    console.error(
                      `Failed to fetch signed URL for Criminal Detail ID ${detail.criminalDetailID}:`,
                      error,
                    );
                    return { id: detail.criminalDetailID, signedUrl: null };
                  }
                } else {
                  return { id: detail.criminalDetailID, signedUrl: null };
                }
              },
            );

            const resolvedUrls = await Promise.all(urlPromises);
            setVisibleSubCriminalDetailUrls(resolvedUrls);
          } catch (error) {
            console.error(
              `Failed to fetch signed URL for criminal details`,
              error,
            );
          }

          // get reference detail urls
          try {
            const urlPromises = referenceDetails.map(async (detail) => {
              if (
                detail.completedFormUrl !== "" &&
                detail.completedFormUrl != null
              ) {
                try {
                  const signedUrl = await HttpService.getFile(
                    "reference-detail-forms",
                    detail.referenceDetailID.toString(),
                    detail.completedFormUrl,
                  );
                  return { id: detail.referenceDetailID, signedUrl };
                } catch (error) {
                  console.error(
                    `Failed to fetch signed URL for Reference Detail ID ${detail.referenceDetailID}:`,
                    error,
                  );
                  return { id: detail.referenceDetailID, signedUrl: null };
                }
              } else {
                return { id: detail.referenceDetailID, signedUrl: null };
              }
            });

            const resolvedUrls = await Promise.all(urlPromises);
            setReferenceDetailUrls(resolvedUrls);

            // look for any other orders with matching DOB and last 4 of SSN that has a status of 6
            const openResult = await checkForOpenSubOrder(order.orderID);
            setOpenSubOrder(openResult);
          } catch (error) {
            console.error(
              `Failed to fetch signed URL for reference details`,
              error,
            );
          }

          const urlResults = await Promise.all(urlPromises);
          const urlMap: Record<number, string> = Object.fromEntries(
            urlResults
              .filter(({ signedUrl }) => signedUrl !== null)
              .map(({ id, signedUrl }) => [Number(id), signedUrl as string]),
          );

          setSignedUrls(urlMap);
        }
      } catch (error) {
        console.error("Error fetching client:", error);
        setErrorMessage("Failed to load order data.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrderData();
  }, [orderId, containerMap, navigate]);

  useEffect(() => {
    const fetchReferenceLetters = async () => {
      try {
        if (referenceDetails.length > 0) {
          const lettersMap = await loadReferenceLetters(referenceDetails);
          setAllLetters(lettersMap);
        }
      } catch (error) {
        console.error("Error fetching reference letters:", error);
      }
    };

    fetchReferenceLetters();
  }, [referenceDetails]);

  const handleShowLetters = (referenceDetailID: number) => {
    if (showLettersFor === referenceDetailID) {
      setShowLettersFor(null);
    } else {
      setShowLettersFor(referenceDetailID);
    }
  };

  const getServiceNames = (type: number): string[] => {
    const sorted = sortOrderServices(orderServices);

    return sorted
      .map((orderService) => {
        const service = services.find(
          (s) => s.serviceID === orderService.serviceID,
        );
        if (!service) {
          console.warn(
            `Service not found for ServiceID: ${orderService.serviceID}`,
          );
        }
        return service;
      })
      .filter((service) => {
        if (!service) return false;

        const isReferenceService = service.referenceYears !== null;
        const isSubscriptionService = service.subscriptionService === true;

        if (type === 1) {
          return !isReferenceService && !isSubscriptionService;
        } else if (type === 2) {
          return isReferenceService;
        } else if (type === 3) {
          return isSubscriptionService;
        }

        return false;
      })
      .map((service) => service!.serviceName);
  };

  const renderFormButtons = () => {
    if (!orderForms || orderForms.length === 0) {
      return null;
    }

    const allowedTypes: number[] = [];
    if (referenceOrder?.referenceOrderID && referenceOrder.adminReviewed) {
      allowedTypes.push(6);
    }
    if (criminalOrder?.criminalOrderID && criminalOrder.adminReviewed) {
      allowedTypes.push(2);
    }
    if (allowedTypes.length === 0) {
      return null;
    }
    const visibleForms = orderForms
      .filter((form) => allowedTypes.includes(form.orderFormTypeID))
      .sort((a, b) => a.orderFormTypeID - b.orderFormTypeID);

    return (
      <>
        <div className="text-container-header" style={{ flex: "0 0 100%" }}>
          Candidate Forms
        </div>
        {visibleForms.map((orderForm) => {
          const orderFormType = orderFormTypes.find(
            (t) => t.orderFormTypeID === orderForm.orderFormTypeID,
          );
          const signedUrl = signedUrls[orderForm.orderFormID];

          let warningClass = "";
          if (
            referenceOrder?.canQCrimAdmit &&
            orderFormType?.orderFormTypeID === 6
          ) {
            warningClass = "btn-red";
          }

          if (orderFormType && signedUrl) {
            return (
              <button
                key={orderForm.orderFormID}
                className={`smbtn ${warningClass}`}
                onClick={() =>
                  handleFormButtonClick(orderForm.orderFormID, signedUrl)
                }
              >
                {orderFormType.description || "No Description"}
                <i className="btn-icon bi bi-box-arrow-up-right"></i>
              </button>
            );
          }

          return (
            <button key={orderForm.orderFormID} className="smbtn" disabled>
              {orderFormType?.description}
            </button>
          );
        })}
      </>
    );
  };

  const handleFormButtonClick = (
    orderFormID: number,
    formLink: string | null,
  ) => {
    if (formLink) {
      window.open(formLink, "_blank");
    } else {
      console.log(`Navigate to form with ID ${orderFormID}`);
    }
  };

  const handleCheckAll = (isChecked: boolean) => {
    setCheckAll(isChecked);

    setReferenceDetails((prevData) =>
      prevData.map((item) => ({
        ...item,
        downloadChecked: isChecked,
      })),
    );
  };

  const handleDownloadResponses = async () => {
    try {
      setLoadingSpinner(true);
      setCheckAll(false);

      const referenceDetailIDs = referenceDetails
        .filter((rd) => rd.downloadChecked)
        .map((rd) => rd.referenceDetailID);

      const pdfBlob = await downloadReferenceResponses(referenceDetailIDs);

      if (pdfBlob) {
        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "ReferenceResponses.pdf");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setLoading(false);
      }
    } catch (error) {
      console.error("Error downloading PDF:", error);
    } finally {
      setLoadingSpinner(false);
    }
  };

  const handleSingleCheck = (id: number, isChecked: boolean) => {
    setReferenceDetails((prevData) =>
      prevData.map((item) =>
        item.referenceDetailID === id
          ? { ...item, downloadChecked: isChecked }
          : item,
      ),
    );
  };

  return (
    <div className="content-container">
      {order && (
        <div className="header-container">
          <h1>
            {(order.applican_link || order.candidat_link) && "*"}
            {order?.candidateFirstName} {order?.candidateLastName}
          </h1>
          <div className="page-items-container"></div>
          {order?.orderStatusID === 4 && (
            <div className="status-tag-past">COMPLETED</div>
          )}
        </div>
      )}

      {/* Letters Popup */}
      {showLettersFor && (
        <>
          <div
            className="background-mask"
            onClick={() => setShowLettersFor(null)}
          ></div>
          <div className="filter-popup">
            <div className="close-button-container">
              <button
                className="close-button"
                onClick={() => setShowLettersFor(null)}
              >
                <i className="bi bi-x-circle"></i>
              </button>
            </div>
            <div className="filter-header">
              <h2>Contact Dates</h2>
            </div>
            <div className="filter-body">
              {allLetters[showLettersFor]?.length ? (
                <div className="filter-letter-container">
                  {allLetters[showLettersFor].map((letter, index) => (
                    <div key={`letter-${index}`}>
                      <div className="field-long" style={{ flex: "0 0 65%" }}>
                        <label className="field-label">
                          {`${ordinal(index + 1)} Contact`}:
                        </label>
                        <div className="field-input">
                          <input
                            className="form-control"
                            type="text"
                            value={formatDate(
                              new Date(letter.letterDate),
                              "us",
                            )}
                            disabled
                            readOnly
                          />
                        </div>
                      </div>
                      <div style={{ flex: "0 0 20%", alignSelf: "center" }}>
                        {letter.certified ? "(Certified)" : ""}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p>No contact dates found.</p>
              )}
            </div>
          </div>
        </>
      )}

      {loading ? (
        <div className="loading-divider">
          <div className="loading-bar"></div>
        </div>
      ) : (
        <>
          <div className="divider"></div>
          {openSubOrder && (
            <>
              <div className="error highlight-text">
                <b>
                  There is an active subscription service for this candidate
                  attached to another order.
                </b>
              </div>
              <br></br>
              <br></br>
            </>
          )}

          <div className="data-container">
            {order && (
              <>
                <div
                  className="text-container-header"
                  style={{ flex: "0 0 100%" }}
                >
                  Order
                </div>
                <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                  <label className="field-label">Initiated Date:</label>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="text"
                      value={formatDate(new Date(order.initiatedDate), "us")}
                      disabled
                      readOnly
                    />
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                  <div style={{ display: "flex", gap: "5px" }}>
                    <label className="field-label">Received Date:</label>
                    <ToolTip
                      tooltipText="The date the forms were returned by the candidate."
                      placement="right"
                      className="info-icon-spec"
                    ></ToolTip>
                  </div>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="date"
                      value={formatDate(order.receivedDate)}
                      disabled
                    />
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                  <label className="field-label">Completed Date:</label>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="text"
                      value={
                        order.completedDate
                          ? formatDate(new Date(order.completedDate), "us")
                          : ""
                      }
                      disabled
                      readOnly
                    />
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                  <label className="field-label">Sent By Mail:</label>
                  <div>
                    <input
                      className={`form-check-input`}
                      type="checkbox"
                      checked={order.sendByMail || false}
                      disabled
                      readOnly
                    />
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 60%" }}>
                  <label className="field-label">Designation:</label>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="text"
                      value={order.designation ? order.designation : ""}
                      disabled
                    />
                  </div>
                </div>
                <div
                  className="text-container-header"
                  style={{ flex: "0 0 100%" }}
                >
                  Candidate
                </div>
                <div className="field-stacked" style={{ flex: "0 0 10%" }}>
                  <label className="field-label">Title:</label>
                  <div className="field-input">
                    <input
                      className={`form-control`}
                      type="text"
                      value={order.candidateTitle}
                      disabled
                      readOnly
                    />
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                  <label className="field-label">First Name:</label>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="text"
                      value={order.candidateFirstName}
                      disabled
                      readOnly
                    />
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 10%" }}>
                  <label className="field-label">Middle Name:</label>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="text"
                      value={order.candidateMI ? order.candidateMI : ""}
                      disabled
                      readOnly
                    />
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                  <label className="field-label">Last Name:</label>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="text"
                      value={order.candidateLastName}
                      disabled
                      readOnly
                    />
                  </div>
                </div>
                <div
                  className="field-stacked"
                  style={{ flex: "0 0 20%" }}
                ></div>
                <div className="field-stacked" style={{ flex: "0 0 40%" }}>
                  <label className="field-label">Email:</label>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="text"
                      value={order.candidateEmail ? order.candidateEmail : ""}
                      disabled
                      readOnly
                    />
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                  <label className="field-label">Phone:</label>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="text"
                      value={order.candidatePhone ? order.candidatePhone : ""}
                      disabled
                      readOnly
                    />
                  </div>
                </div>
                <div
                  className="field-stacked"
                  style={{ flex: "0 0 20%" }}
                ></div>
                <div className="field-stacked" style={{ flex: "0 0 30%" }}>
                  <label className="field-label">Address:</label>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="text"
                      value={
                        order.candidateAddress ? order.candidateAddress : ""
                      }
                      disabled
                      readOnly
                    />
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 30%" }}>
                  <label className="field-label">Address 2:</label>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="text"
                      value={
                        order.candidateAddress2 ? order.candidateAddress2 : ""
                      }
                      disabled
                      readOnly
                    />
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                  <label className="field-label">City:</label>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="text"
                      value={order.candidateCity ? order.candidateCity : ""}
                      disabled
                      readOnly
                    />
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                  <label className="field-label">State:</label>
                  <div className="field-input">
                    <input
                      className="form-select"
                      type="text"
                      value={order.candidateState ? order.candidateState : ""}
                      disabled
                      readOnly
                    />
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                  <label className="field-label">Zip Code:</label>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="text"
                      value={
                        order.candidateZipCode ? order.candidateZipCode : ""
                      }
                      disabled
                      readOnly
                    />
                  </div>
                </div>
                {renderFormButtons()}{" "}
                {referenceOrder && referenceOrder.clientNotifiedDate && (
                  <div style={{ color: "red" }}>
                    <b>Yes Answer Reported</b>
                  </div>
                )}
              </>
            )}
          </div>

          {criminalOrder &&
            criminalOrder.criminalOrderID !== 0 &&
            criminalDetails.length > 0 && (
              <>
                {criminalOrder.finalReportDate == null ? (
                  <div className="data-container">
                    <div
                      className="text-container-header"
                      style={{ flex: "0 0 100%" }}
                    >
                      Criminal/Public Record Services Requested
                    </div>
                    <h3 className="text-body">
                      <div>
                        Report in Process. Information will become visible on
                        completion.
                      </div>
                    </h3>
                  </div>
                ) : (
                  <div className="data-container">
                    <div
                      className="text-container-header"
                      style={{ flex: "0 0 50%" }}
                    >
                      Criminal/Public Record Services Requested
                    </div>
                    <div className="field-long" style={{ flex: "0 0 20%" }}>
                      <label className="field-label">Years:</label>
                      <div className="field-input">
                        <input
                          className="form-control"
                          type="text"
                          value={criminalOrder.years}
                          disabled
                          readOnly
                        />
                      </div>
                    </div>
                    <div style={{ flex: "0 0 100%" }}>
                      <ul>
                        {getServiceNames(1).map((serviceName, index) => (
                          <li key={index}>{serviceName}</li>
                        ))}
                      </ul>
                    </div>
                    {criminalFinalUrl !== null && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "end",
                        }}
                      >
                        <button
                          className="oxbtn btn-purple"
                          onClick={() => {
                            if (criminalFinalUrl) {
                              window.open(criminalFinalUrl, "_blank");
                            }
                          }}
                        >
                          View Final Report
                          <i className="btn-icon bi bi-box-arrow-up-right"></i>
                        </button>
                      </div>
                    )}

                    <div className="field-stacked" style={{ flex: "0 0 30%" }}>
                      <label className="field-label">Final Report Date:</label>
                      <div className="field-input">
                        <input
                          className="form-control"
                          type="text"
                          value={formatDate(criminalOrder.finalReportDate)}
                          disabled
                          readOnly
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

          {subscriptionOrder && subscriptionOrder.subscriptionOrderID !== 0 && (
            <>
              <div className="data-container">
                <div
                  className="text-container-header"
                  style={{ flex: "0 0 50%" }}
                >
                  Subscription Services Requested
                </div>
                <div style={{ flex: "0 0 100%" }}>
                  <ul>
                    {getServiceNames(3).map((serviceName, index) => (
                      <li key={index}>{serviceName}</li>
                    ))}
                  </ul>
                </div>
                {subscriptionDetails?.length !== 0 && (
                  <div className="table-wrapper">
                    <div className="table-container">
                      <table className="table" style={{ cursor: "pointer" }}>
                        <thead>
                          <tr>
                            <th style={{ width: "180px" }}>Service</th>
                            <th
                              style={{
                                width: "120px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  gap: "5px",
                                }}
                              >
                                <div>Initiated</div>
                              </div>
                            </th>
                            <th style={{ width: "120px" }}>End</th>
                          </tr>
                        </thead>
                        <tbody>
                          {subscriptionDetails?.map((detail) => {
                            const service = services.find(
                              (s) => s.serviceID === detail.serviceID,
                            );
                            return (
                              <tr key={detail.subscriptionDetailID}>
                                <td>
                                  {service
                                    ? service.serviceName
                                    : `Unknown Service (ID: ${detail.serviceID})`}
                                </td>
                                <td>
                                  {formatDate(detail.initiatedDate, "us")}
                                </td>
                                <td>{formatDate(detail.stopDate, "us")}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {visibleSubCriminalDetails?.length !== 0 && (
                  <div className="table-wrapper">
                    <div className="table-container">
                      <table className="table">
                        <thead>
                          <tr>
                            <th style={{ width: "200px" }}>
                              Subscription Alert
                            </th>
                            <th style={{ width: "150px" }}></th>
                            <th style={{ width: "150px" }}></th>
                            <th style={{ width: "150px" }}>Received</th>
                            <th style={{ width: "150px" }}>Alert</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visibleSubCriminalDetails?.map((detail) => {
                            const service = services.find(
                              (s) => s.serviceID === detail.serviceID,
                            );
                            return (
                              <tr key={detail.criminalDetailID}>
                                <td>
                                  {service
                                    ? service.serviceName
                                    : `Unknown Service (ID: ${detail.serviceID})`}
                                </td>
                                <td>{detail.resultsFieldOne}</td>
                                <td>{detail.resultsFieldTwo}</td>
                                <td>
                                  {" "}
                                  {formatDate(detail.receivedDate, "us")}
                                </td>
                                <td>
                                  {(() => {
                                    const signedUrlEntry =
                                      visibleSubCriminalDetailUrls.find(
                                        (entry) =>
                                          entry.id === detail.criminalDetailID,
                                      );

                                    return signedUrlEntry?.signedUrl ? (
                                      <button
                                        className="xsbtn btn-red"
                                        onClick={() => {
                                          if (signedUrlEntry.signedUrl) {
                                            window.open(
                                              signedUrlEntry.signedUrl as string,
                                              "_blank",
                                            );
                                          }
                                        }}
                                      >
                                        View{" "}
                                        <i className="btn-sm-icon bi bi-box-arrow-up-right"></i>
                                      </button>
                                    ) : (
                                      "-"
                                    );
                                  })()}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {referenceOrder && referenceOrder.referenceOrderID !== 0 && (
            <>
              {!referenceOrder.adminReviewed ? (
                <div className="data-container">
                  <div
                    className="text-container-header"
                    style={{ flex: "0 0 100%" }}
                  >
                    Reference Service Requested
                  </div>
                  <h3 className="text-body">
                    <div>
                      Information will become visible after Oxford review.
                    </div>
                  </h3>
                </div>
              ) : (
                <div className="data-container">
                  <div
                    className="text-container-header"
                    style={{ flex: "0 0 30%" }}
                  >
                    Reference Service Requested
                  </div>
                  <div className="field-long" style={{ flex: "0 0 20%" }}>
                    <label className="field-label">Years:</label>
                    <div className="field-input">
                      <input
                        className="form-control"
                        type="text"
                        value={referenceOrder.years}
                        disabled
                        readOnly
                      />
                    </div>
                  </div>
                  <div style={{ flex: "0 0 100%" }}>
                    <ul>
                      {getServiceNames(2).map((serviceName, index) => (
                        <li key={index}>{serviceName}</li>
                      ))}
                    </ul>
                  </div>
                  {referenceOrder.finalReportDate &&
                    referenceOrder.finalReportUrl && (
                      <>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "end",
                          }}
                        >
                          <button
                            className="oxbtn btn-purple"
                            onClick={() => {
                              if (referenceFinalUrl) {
                                window.open(referenceFinalUrl, "_blank");
                              }
                            }}
                          >
                            View Final Report
                            <i className="btn-icon bi bi-box-arrow-up-right"></i>
                          </button>
                        </div>
                        <div
                          className="field-stacked"
                          style={{ flex: "0 0 30%" }}
                        >
                          <label className="field-label">
                            Final Report Date:
                          </label>
                          <div className="field-input">
                            <input
                              className="form-control"
                              type="text"
                              value={formatDate(referenceOrder.finalReportDate)}
                              disabled
                              readOnly
                            />
                          </div>
                        </div>
                      </>
                    )}
                  <div
                    className="table-wrapper"
                    style={{ marginBottom: "0px" }}
                  >
                    <div className="table-container">
                      <table className="table" style={{ cursor: "pointer" }}>
                        <thead>
                          <tr>
                            <th style={{ width: "200px" }}>Name</th>
                            <th style={{ width: "120px" }}>Initial Letter</th>
                            <th style={{ width: "120px" }}>Received</th>
                            <th
                              style={{
                                width: "100px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  gap: "5px",
                                }}
                              >
                                <div>Red Flag</div>
                                <ToolTip
                                  tooltipText="Response includes 'yes' answer(s) or negative comments"
                                  placement="right"
                                  className="info-icon-spec"
                                ></ToolTip>
                              </div>
                            </th>
                            <th style={{ width: "200px" }}>Contact</th>
                            <th
                              style={{
                                width: "120px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  gap: "5px",
                                }}
                              >
                                <div>Notified</div>
                                <ToolTip
                                  tooltipText="Response includes 'yes' answer(s) or positive/negative comments for review"
                                  placement="top"
                                  className="info-icon-spec"
                                ></ToolTip>
                              </div>
                            </th>
                            <th style={{ width: "150px" }}>
                              <div
                                style={{
                                  display: "flex",
                                  gap: "5px",
                                }}
                              >
                                <div>Response</div>
                              </div>
                            </th>
                            <th style={{ width: "50px" }}>
                              <div
                                style={{
                                  display: "flex",
                                  gap: "5px",
                                }}
                              >
                                <input
                                  className={`form-check-input`}
                                  type="checkbox"
                                  checked={checkAll}
                                  onChange={(e) =>
                                    handleCheckAll(e.target.checked)
                                  }
                                />
                                <ToolTip
                                  tooltipText="Download Multiple: Select the checkboxes for the responses you wish to download, or use the header checkbox to select all. Then click the Download Selected Responses button."
                                  placement="top"
                                  className="info-icon-spec"
                                ></ToolTip>
                              </div>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {referenceDetails?.length === 0 ? (
                            <tr>
                              <td colSpan={7} style={{ textAlign: "center" }}>
                                No reference details found.
                              </td>
                            </tr>
                          ) : (
                            referenceDetails?.map((detail) => {
                              const letters =
                                allLetters[detail.referenceDetailID] || [];
                              const initialLetter =
                                letters.length > 0 ? letters[0] : null;
                              return (
                                <tr
                                  key={detail.referenceDetailID}
                                  onClick={() =>
                                    handleShowLetters(detail.referenceDetailID)
                                  }
                                >
                                  <td>{detail.placeName}</td>
                                  <td>
                                    {formatDate(
                                      initialLetter?.letterDate,
                                      "us",
                                    )}
                                  </td>
                                  <td>
                                    {formatDate(detail.letterReturned, "us")}
                                  </td>
                                  <td
                                    style={{
                                      color: detail.redFlag ? "red" : "inherit",
                                      fontWeight: detail.redFlag
                                        ? "bold"
                                        : "normal",
                                    }}
                                  >
                                    {detail.redFlag ? "Red Flag" : ""}
                                  </td>
                                  <td>{detail.referenceName}</td>
                                  <td>
                                    {formatDate(
                                      detail.clientNotifiedDate,
                                      "us",
                                    )}
                                  </td>
                                  <td>
                                    {detail.adminReviewed &&
                                      (() => {
                                        const signedUrlEntry =
                                          referenceDetailUrls.find(
                                            (entry) =>
                                              entry.id ===
                                              detail.referenceDetailID,
                                          );

                                        return signedUrlEntry?.signedUrl ? (
                                          <button
                                            className={`xsbtn btn-purple ${
                                              !detail.adminReviewed
                                                ? "disabled"
                                                : ""
                                            }`}
                                            onClick={(e) => {
                                              if (signedUrlEntry.signedUrl) {
                                                e.stopPropagation();
                                                window.open(
                                                  signedUrlEntry.signedUrl as string,
                                                  "_blank",
                                                );
                                              }
                                            }}
                                            disabled={!detail.adminReviewed}
                                          >
                                            View{" "}
                                            <i className="btn-sm-icon bi bi-box-arrow-up-right"></i>
                                          </button>
                                        ) : (
                                          "-"
                                        );
                                      })()}
                                  </td>
                                  <td>
                                    <input
                                      className={`form-check-input`}
                                      type="checkbox"
                                      checked={detail.downloadChecked}
                                      onChange={(e) => {
                                        handleSingleCheck(
                                          detail.referenceDetailID,
                                          e.target.checked,
                                        );
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                      }}
                                    />
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      width: "100%",
                      justifyContent: "right",
                    }}
                  >
                    <button
                      className="smbtn btn-purple"
                      onClick={handleDownloadResponses}
                    >
                      Download Selected Responses
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
          {errorMessage && (
            <div
              className="alert alert-danger alert-dismissible fade show"
              role="alert"
            >
              {errorMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setErrorMessage("")}
                aria-label="Close"
              ></button>
            </div>
          )}
        </>
      )}
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default ClientViewOrder;
